import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { StringParam, withDefault, useQueryParams } from 'use-query-params';
import { RTabs } from '@welldev-group/rcl-rtabs';
import { I18nText } from '@welldev-group/react-comp-lib';

import withQueryParamsProvider from '../../../../../shared/components/withQueryParamsProvider';
import Channels from '../../../Channel';
import ConfigurationTab from './ConfigurationTab';
import TransactionListOverview from '../../../Billing/TransactionListOverview';

import LogTab from './LogTab';
import UserTab from './UserTab';
import PlanTab from './PlanTab';
import OverviewTab from './OverviewTab';
import WorkspaceTab from './WorkspaceTab';

const AffiliateTabs = props => {
  const tabRef = useRef(null);
  const [selectedTabKey, setSelectedTabKey] = useState(0);
  const [query] = useQueryParams({
    tab: withDefault(StringParam, ''),
  });

  const { tab } = query;

  const TAB_MAPPER = {
    overview: 0,
    users: 1,
    workspaces: 2,
    plans: 3,
    payments: 4,
    channels: 5,
    configuration: 6,
    logs: 7,
  };

  const setSelectedTabKeyOnClick = tabIndex => {
    setSelectedTabKey(tabIndex);
  };

  const browseToAssociatedTab = newTab => {
    const currentTab = TAB_MAPPER[newTab] || TAB_MAPPER.overview;
    tabRef.current.onChangeTab(currentTab);
  };

  const items = [
    {
      title: (
        <I18nText id="affiliate_three_sixty.affiliate.tabs.overview.tab_header" />
      ),
      content: (
        <OverviewTab
          id={props.id}
          affiliate={props.affiliate}
          updateAffiliate={props.updateAffiliate}
        />
      ),
    },
    {
      title: (
        <I18nText id="affiliate_three_sixty.affiliate.tabs.users.tab_header" />
      ),
      content: (
        <UserTab
          id={props.id}
          urls={props.urls}
          currentUserId={props.currentUserId}
          signedInAsSuperAdmin={props.signedInAsSuperAdmin}
          permissionTemplateOptions={props.permissionTemplateOptions}
          permissionOptions={props.permissionOptions}
          displayInvites={props.displayInvites}
          userAffiliateId={props.userAffiliateId}
        />
      ),
    },
    {
      title: (
        <I18nText id="affiliate_three_sixty.affiliate.tabs.workspaces.tab_header" />
      ),
      content: (
        <WorkspaceTab id={props.id} userAffiliateId={props.userAffiliateId} />
      ),
    },
    {
      title: (
        <I18nText id="affiliate_three_sixty.affiliate.tabs.plans.tab_header" />
      ),
      content: <PlanTab id={props.id} />,
    },
    {
      title: (
        <I18nText id="affiliate_three_sixty.affiliate.tabs.payments.tab_header" />
      ),
      content: (
        <div className="col-12">
          <TransactionListOverview
            affiliateId={props.id}
            canRecordWireTransfer={true}
            fromAffiliate360={true}
          />
        </div>
      ),
    },
    {
      title: (
        <I18nText id="affiliate_three_sixty.affiliate.tabs.channels.tab_header" />
      ),
      content: (
        <Channels
          affiliateId={props.id}
          fetchAffiliate={props.fetchAffiliate}
          fromAffiliate360={true}
        />
      ),
    },
    {
      title: I18n.t(
        'components.affiliate_three_sixty.affiliate.tabs.configuration.tab_header',
      ),
      content: (
        <ConfigurationTab affiliateId={props.id} fromAffiliate360={true} />
      ),
    },
    {
      title: (
        <I18nText id="affiliate_three_sixty.affiliate.tabs.logs.tab_header" />
      ),
      content: <LogTab id={props.id} />,
    },
    // TODO: As we are launching we are commenting out coming soons, Story where it will be done: AF-916
    // {
    //   title: (
    //     <I18nText id="affiliate_three_sixty.affiliate.tabs.support_tickets.tab_header" />
    //   ),
    //   content: <div>Support Tickets Coming Soon</div>,
    // },
  ];
  const getItems = () => {
    return items.map(({ title, content }, index) => ({
      key: index,
      title,
      content: <div className="affiliate-tabs__container">{content}</div>,
    }));
  };

  useEffect(() => {
    browseToAssociatedTab(tab);
  }, []);

  return (
    <div className="affiliate-tabs">
      <RTabs
        items={getItems()}
        selectedTabKey={selectedTabKey}
        onChange={setSelectedTabKeyOnClick}
        ref={tabRef}
      />
    </div>
  );
};

AffiliateTabs.defaultProps = {
  permissionTemplateOptions: [],
};

AffiliateTabs.propTypes = {
  id: PropTypes.number.isRequired,
  fetchAffiliate: PropTypes.func.isRequired,
  affiliate: PropTypes.shape({
    bookingsTotal: PropTypes.number,
    travelersTotal: PropTypes.number,
    currentWorkspacesSize: PropTypes.number,
    chargeableChannelCount: PropTypes.number,
    ndcChannelCount: PropTypes.number,
    clientName: PropTypes.string,
    briefing: PropTypes.string,
    briefingUpdatedAt: PropTypes.string,
  }).isRequired,
  updateAffiliate: PropTypes.func.isRequired,
  signedInAsSuperAdmin: PropTypes.bool.isRequired,
  userAffiliateId: PropTypes.number.isRequired,
  urls: PropTypes.shape({
    users: PropTypes.string.isRequired,
    invites: PropTypes.string.isRequired,
    teams: PropTypes.string.isRequired,
  }).isRequired,
  permissionOptions: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string))
    .isRequired,
  permissionTemplateOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ),
  currentUserId: PropTypes.number.isRequired,
  displayInvites: PropTypes.bool.isRequired,
};

export default withQueryParamsProvider(AffiliateTabs);
